import { useEffect, useState } from 'react';

import { useStoreContext } from 'features/store';
import { DEFAULT_SEARCH_RADIUS_IN_METERS } from 'features/store/constants';
import { useNearbyRestaurants } from 'features/store/pages/store-locator/hooks/helpers/use-restaurants';

import { IRestaurant } from '@rbi-ctg/store';
import { OperationalStatus } from 'generated/graphql-gateway';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { useAutoSelectStoreUtils } from './use-auto-select-store-utils';

/**
 *
 * Auto selects closest restaurant based on navigator geolocation
 *
 **/
export const useAutoSelectStore = () => {
  const [restaurantToConfirm, setRestaurantToConfirm] = useState<IRestaurant | null>(null);
  const { noStoreSelected, store } = useStoreContext();
  const { loadCurrentPosition, activeCoordinates } = useGeolocation();

  const defaultDiagonal =
    useFlag(LaunchDarklyFlag.RADIUS_FOR_STORE_LOCATOR) || DEFAULT_SEARCH_RADIUS_IN_METERS;

  const { stores, error, fetch: fetchNearbyStores, loading } = useNearbyRestaurants();

  const didFetch = Boolean(stores || error || loading);

  const { getFirstAvailableStore, handleConfirmRestaurant } = useAutoSelectStoreUtils();

  // Only use native prompt on mount if not mobile sized device (including mobile running web app)
  // and permission for geolocation unknown
  const confirmRestaurant = (confirmed: boolean) => {
    handleConfirmRestaurant({ confirmed, restaurantToConfirm, onSelect: setRestaurantToConfirm });
  };

  useEffect(() => {
    if (!noStoreSelected || didFetch) {
      return;
    }

    if (!activeCoordinates) {
      loadCurrentPosition();
      return;
    }

    if (activeCoordinates) {
      fetchNearbyStores(
        {
          first: 10,
          status: OperationalStatus.OPEN,
          coordinates: {
            searchRadius: defaultDiagonal,
            userLat: Number(activeCoordinates.lat.toFixed(3)),
            userLng: Number(activeCoordinates.lng.toFixed(3)),
          },
        },
        { allowFromCache: false }
      ).then(async nearbyStores => {
        if (!nearbyStores?.length) {
          return;
        }

        const firstAvailableStore = await getFirstAvailableStore(nearbyStores as IRestaurant[]);

        if (!firstAvailableStore) {
          return;
        }

        setRestaurantToConfirm(firstAvailableStore);
      });
    }
  }, [
    noStoreSelected,
    store,
    getFirstAvailableStore,
    activeCoordinates,
    fetchNearbyStores,
    defaultDiagonal,
    loadCurrentPosition,
    didFetch,
  ]);

  return {
    confirmRestaurant,
    restaurantToConfirm,
  };
};
